import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuthModal } from "@/hooks/useAuthModal";
import { toast } from "sonner";
import { Star, ThumbsUp, ThumbsDown, MoreVertical, User, Smile, ChevronDown, Flag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import verifiedBadge from "@/assets/verified-badge.png";
import { ReviewPost } from "./ReviewPost";
import { ThreadedComment } from "./ThreadedComment";
import { ReportCommentDialog } from "./ReportCommentDialog";
interface DiscussionSectionProps {
  macroId: string;
  user: any;
  sortBy: "newest" | "top" | "helpful";
  onSortChange: (sort: "newest" | "top" | "helpful") => void;
  onReviewUpdate?: () => void;
}
export const DiscussionSection = ({
  macroId,
  user,
  sortBy,
  onSortChange,
  onReviewUpdate
}: DiscussionSectionProps) => {
  const { openAuthModal } = useAuthModal();
  const [reviews, setReviews] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [content, setContent] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);

  // Fetch current user's profile
  useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      if (user?.id) {
        const {
          data
        } = await supabase.from("profiles").select("username, avatar_url").eq("id", user.id).single();
        if (data) {
          setCurrentUserProfile(data);
        }
      }
    };
    fetchCurrentUserProfile();
  }, [user?.id]);
  useEffect(() => {
    fetchData();
  }, [macroId, sortBy]);
  const fetchData = async () => {
    // Fetch reviews with profiles and vote counts
    const {
      data: rawReviews
    } = await supabase.from("reviews").select("*").eq("macro_id", macroId);
    if (rawReviews) {
      const userIds = Array.from(new Set(rawReviews.map(r => r.user_id).filter(Boolean)));
      let profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const {
          data: profs
        } = await supabase.from("public_profiles").select("id, username, avatar_url, verified").in("id", userIds);
        (profs || []).forEach((p: any) => {
          profilesMap[p.id] = p;
        });
      }

      // Fetch vote counts and user votes for reviews (we'll create this table next)
      const reviewsWithData = await Promise.all(rawReviews.map(async (r: any) => {
        const {
          count: upvotes
        } = await (supabase as any).from("review_votes").select("*", {
          count: "exact",
          head: true
        }).eq("review_id", r.id).eq("vote_type", "up");
        const {
          count: downvotes
        } = await (supabase as any).from("review_votes").select("*", {
          count: "exact",
          head: true
        }).eq("review_id", r.id).eq("vote_type", "down");
        let userVote = null;
        if (user) {
          const {
            data: vote
          } = await (supabase as any).from("review_votes").select("vote_type").eq("review_id", r.id).eq("user_id", user.id).maybeSingle();
          userVote = vote?.vote_type || null;
        }
        return {
          ...r,
          profiles: profilesMap[r.user_id] || null,
          vote_count: (upvotes || 0) - (downvotes || 0),
          user_vote: userVote
        };
      }));
      setReviews(reviewsWithData);
    }

    // Fetch comments with profiles and build tree structure
    const {
      data: rawComments
    } = await supabase.from("comments").select("*").eq("macro_id", macroId);
    if (rawComments) {
      const userIds = Array.from(new Set(rawComments.map(c => c.user_id).filter(Boolean)));
      let profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const {
          data: profs
        } = await supabase.from("public_profiles").select("id, username, avatar_url, verified").in("id", userIds);
        (profs || []).forEach((p: any) => {
          profilesMap[p.id] = p;
        });
      }

      // Fetch vote data for all comments
      const commentsWithData = await Promise.all(rawComments.map(async (c: any) => {
        const {
          count: upvotes
        } = await (supabase as any).from("comment_votes").select("*", {
          count: "exact",
          head: true
        }).eq("comment_id", c.id).eq("vote_type", "up");
        const {
          count: downvotes
        } = await (supabase as any).from("comment_votes").select("*", {
          count: "exact",
          head: true
        }).eq("comment_id", c.id).eq("vote_type", "down");
        let userVote = null;
        if (user) {
          const {
            data: vote
          } = await (supabase as any).from("comment_votes").select("vote_type").eq("comment_id", c.id).eq("user_id", user.id).maybeSingle();
          userVote = vote?.vote_type || null;
        }
        return {
          ...c,
          profiles: profilesMap[c.user_id] || null,
          vote_count: (upvotes || 0) - (downvotes || 0),
          user_vote: userVote
        };
      }));

      // Build tree structure
      const buildTree = (parentId: string | null): any[] => {
        return commentsWithData.filter(c => c.parent_id === parentId).map(c => ({
          ...c,
          replies: buildTree(c.id)
        }));
      };
      const rootComments = buildTree(null);
      setComments(rootComments);
    }
    setLoading(false);
  };
  const handleSubmit = async () => {
    if (!user) {
      toast.error("Please sign in to post");
      return;
    }
    if (!content.trim()) {
      toast.error("Please enter some content");
      return;
    }
    if (rating !== null) {
      // Submit as review with rating via edge function
      const { data, error } = await supabase.functions.invoke('submit-review', {
        body: {
          macro_id: macroId,
          rating,
          comment: content.trim()
        }
      });
      
      if (error || data?.error) {
        const errorMessage = data?.error || error?.message || "Failed to submit review";
        toast.error(errorMessage);
      } else {
        toast.success("Review posted!");
        setContent("");
        setRating(null);
        fetchData();
        onReviewUpdate?.();
      }
    } else {
      // Submit as comment without rating via edge function
      const { data, error } = await supabase.functions.invoke('submit-comment', {
        body: {
          macro_id: macroId,
          content: content.trim(),
          parent_id: null
        }
      });
      
      if (error || data?.error) {
        const errorMessage = data?.error || error?.message || "Failed to post comment";
        toast.error(errorMessage);
      } else {
        toast.success("Comment posted!");
        setContent("");
        fetchData();
      }
    }
  };
  const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;
  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (sortBy === "top") {
      return b.vote_count - a.vote_count;
    }
    if (sortBy === "helpful") {
      // Helpful combines rating quality with community votes
      const aHelpfulness = a.rating * 0.3 + a.vote_count * 0.7;
      const bHelpfulness = b.rating * 0.3 + b.vote_count * 0.7;
      return bHelpfulness - aHelpfulness;
    }
    return b.vote_count - a.vote_count;
  });

  // Filter out comments that belong to reviews (they're shown under ReviewPost)
  const standaloneComments = comments.filter(c => !c.review_id);
  const sortedComments = [...standaloneComments].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (sortBy === "top") {
      return b.vote_count - a.vote_count;
    }
    if (sortBy === "helpful") {
      // For comments, helpful considers votes and engagement (replies)
      const aHelpfulness = a.vote_count + (a.replies?.length || 0) * 0.5;
      const bHelpfulness = b.vote_count + (b.replies?.length || 0) * 0.5;
      return bHelpfulness - aHelpfulness;
    }
    return b.vote_count - a.vote_count;
  });
  return <div className="space-y-6">
      {/* YouTube-style header: Comment count + Sort dropdown */}
      <div className="flex items-center gap-8">
        <h2 className="text-xl font-semibold">
          {(reviews.length + standaloneComments.length).toLocaleString()} Comments
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </div>
          <Select value={sortBy} onValueChange={(v: any) => onSortChange(v)}>
            <SelectTrigger className="w-[120px] border-0 bg-transparent hover:bg-muted/30 focus:ring-0 font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border/50 rounded-xl">
              <SelectItem value="top">Top</SelectItem>
              <SelectItem value="helpful">Most Helpful</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* YouTube-style comment input */}
      {user ? (
        <div className="flex gap-3">
          <Link to={`/profile/${currentUserProfile?.username || 'user'}`}>
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarImage src={currentUserProfile?.avatar_url} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {currentUserProfile?.username?.[0]?.toUpperCase() || <User className="h-5 w-5" />}
              </AvatarFallback>
            </Avatar>
          </Link>
          
          <div className="flex-1 space-y-3">
            <Textarea placeholder="Add a comment..." value={content} onChange={e => setContent(e.target.value)} onFocus={() => setIsCommentFocused(true)} className="min-h-[24px] resize-none border-0 border-b border-muted-foreground/30 bg-transparent focus-visible:ring-0 focus-visible:border-b-foreground transition-colors px-0 text-sm rounded-none" rows={isCommentFocused ? 3 : 1} />
            
            {isCommentFocused && <div className="space-y-4">
                {/* Rating stars and buttons - stacked on mobile, side-by-side on desktop */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-2 justify-end md:justify-start">
                    <span className="text-sm text-muted-foreground">Rate this macro:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setRating(star === rating ? null : star)} className="transition-all hover:scale-110">
                          <Star className={`h-6 w-6 ${rating && star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground hover:text-yellow-400"}`} />
                        </button>)}
                    </div>
                    {rating && <button onClick={() => setRating(null)} className="text-xs text-muted-foreground hover:text-foreground">
                        Clear
                      </button>}
                  </div>

                  <div className="flex items-center gap-2 justify-end md:justify-start">
                    <Button variant="ghost" onClick={() => {
                setIsCommentFocused(false);
                setContent("");
                setRating(null);
              }} className="rounded-full">
                      Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!content.trim()} className="rounded-full bg-primary hover:bg-primary/90">
                      Comment
                    </Button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 py-4">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarFallback className="bg-muted text-muted-foreground">
              <User className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-muted-foreground mb-3">Sign in to leave a review or comment</p>
            <Button onClick={() => openAuthModal()} size="sm">
              Sign In
            </Button>
          </div>
        </div>
      )}

      {/* Comments list */}
      {loading ? <p className="text-sm text-muted-foreground">Loading comments...</p> : <div className="space-y-4">
          {sortedReviews.map(review => <YouTubeStyleComment key={review.id} comment={review} isReview={true} user={user} macroId={macroId} onRefresh={fetchData} currentUserProfile={currentUserProfile} />)}
          {sortedComments.map(comment => <YouTubeStyleComment key={comment.id} comment={comment} isReview={false} user={user} macroId={macroId} onRefresh={fetchData} currentUserProfile={currentUserProfile} />)}
          {reviews.length === 0 && standaloneComments.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">
              No comments yet. Be the first to comment!
            </p>}
        </div>}
    </div>;
};

// YouTube-style comment component
const YouTubeStyleComment = ({
  comment,
  isReview,
  user,
  macroId,
  onRefresh,
  currentUserProfile
}: any) => {
  const { openAuthModal } = useAuthModal();
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const [localVoteCount, setLocalVoteCount] = useState(comment.vote_count || 0);
  const [localUserVote, setLocalUserVote] = useState(comment.user_vote);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const handleVote = async (voteType: "up" | "down") => {
    if (!user) {
      toast.error("Please sign in to vote");
      return;
    }
    const isRemovingVote = localUserVote === voteType;
    if (isRemovingVote) {
      if (isReview) {
        await supabase.from("review_votes").delete().eq("review_id", comment.id).eq("user_id", user.id);
      } else {
        await supabase.from("comment_votes").delete().eq("comment_id", comment.id).eq("user_id", user.id);
      }
      setLocalVoteCount(prev => prev + (voteType === "up" ? -1 : 1));
      setLocalUserVote(null);
    } else {
      let error;
      if (isReview) {
        const result = await supabase.from("review_votes").upsert({
          review_id: comment.id,
          user_id: user.id,
          vote_type: voteType
        }, {
          onConflict: "review_id,user_id"
        });
        error = result.error;
      } else {
        const result = await supabase.from("comment_votes").upsert({
          comment_id: comment.id,
          user_id: user.id,
          vote_type: voteType
        }, {
          onConflict: "comment_id,user_id"
        });
        error = result.error;
      }
      if (error) {
        toast.error("Failed to vote");
        return;
      }
      const previousVote = localUserVote;
      const delta = previousVote === null ? voteType === "up" ? 1 : -1 : voteType === "up" ? 2 : -2;
      setLocalVoteCount(prev => prev + delta);
      setLocalUserVote(voteType);
    }
  };
  const handleReply = async () => {
    if (!user || !replyText.trim()) return;
    
    const { data, error } = await supabase.functions.invoke('submit-comment', {
      body: {
        macro_id: macroId,
        content: replyText.trim(),
        parent_id: isReview ? null : comment.id,
        review_id: isReview ? comment.id : null
      }
    });
    
    if (error || data?.error) {
      const errorMessage = data?.error || error?.message || "Failed to post reply";
      toast.error(errorMessage);
    } else {
      toast.success("Reply posted!");
      setReplyText("");
      setIsReplying(false);
      onRefresh();
    }
  };
  const replyCount = comment.replies?.length || 0;
  return <div className="flex gap-3">
      <Link to={`/profile/${comment.profiles?.username || 'user'}`}>
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage src={comment.profiles?.avatar_url} />
          <AvatarFallback className="bg-primary/10 text-primary">
            {comment.profiles?.username?.[0]?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
      </Link>

      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/profile/${comment.profiles?.username || 'user'}`} className="flex items-center gap-1.5">
            <span className="font-medium text-sm hover:text-primary transition-colors">
              @{comment.profiles?.username || 'user'}
            </span>
            {comment.profiles?.verified && <img src={verifiedBadge} alt="Verified" className="h-4 w-4" />}
          </Link>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), {
            addSuffix: true
          })}
          </span>
        </div>

        {isReview && comment.rating && <div className="flex gap-0.5 mb-1">
            {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`h-3 w-3 ${star <= comment.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />)}
          </div>}

        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
          {isReview ? comment.comment : comment.content}
        </p>

        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full bg-transparent">
            <button onClick={() => handleVote("up")} className={`p-2 hover:bg-muted rounded-l-full transition-colors ${localUserVote === "up" ? "text-primary" : ""}`}>
              <ThumbsUp className="h-4 w-4" />
            </button>
            <span className="text-xs font-medium px-2">{localVoteCount}</span>
            <button onClick={() => handleVote("down")} className={`p-2 hover:bg-muted rounded-r-full transition-colors ${localUserVote === "down" ? "text-destructive" : ""}`}>
              <ThumbsDown className="h-4 w-4" />
            </button>
          </div>

          <Button variant="ghost" size="sm" onClick={() => setIsReplying(!isReplying)} className="rounded-full text-xs font-medium h-8">
            Reply
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 hover:bg-muted rounded-full transition-colors ml-auto">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => {
                  if (!user) {
                    openAuthModal();
                    return;
                  }
                  setShowReportDialog(true);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Flag className="h-4 w-4 mr-2" />
                Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ReportCommentDialog
            open={showReportDialog}
            onOpenChange={setShowReportDialog}
            commentId={comment.id}
            userId={user?.id || null}
          />
        </div>

        {isReplying && user && <div className="flex gap-3 mt-3">
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarImage src={currentUserProfile?.avatar_url} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {currentUserProfile?.username?.[0]?.toUpperCase() || <User className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <Textarea placeholder="Add a reply..." value={replyText} onChange={e => setReplyText(e.target.value)} className="min-h-[60px] text-sm resize-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-foreground px-0" />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => {
              setIsReplying(false);
              setReplyText("");
            }} className="rounded-full">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleReply} disabled={!replyText.trim()} className="rounded-full">
                  Reply
                </Button>
              </div>
            </div>
          </div>}

        {replyCount > 0 && <button onClick={() => setShowReplies(!showReplies)} className="flex items-center gap-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-full px-4 py-2 transition-colors">
            <ChevronDown className={`h-4 w-4 transition-transform ${showReplies ? "rotate-180" : ""}`} />
            {replyCount} {replyCount === 1 ? "reply" : "replies"}
          </button>}

        {showReplies && comment.replies && <div className="space-y-4 mt-4 pl-8 border-l-2 border-border/30">
            {comment.replies.map((reply: any) => <YouTubeStyleComment key={reply.id} comment={reply} isReview={false} user={user} macroId={macroId} onRefresh={onRefresh} currentUserProfile={currentUserProfile} />)}
          </div>}
      </div>
    </div>;
};